import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { TbAward, TbCalendar, TbStar, TbUsers } from 'react-icons/tb'
import { FaArrowRight } from 'react-icons/fa'
import Emoji from '../components/ui/Emoji/Emoji'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

// What renders if the API is down or the table is empty. The live prices come
// from the MentorshipService table (admin.gazar.dev → Mentorship); keep these
// roughly in step, and note that `slug` is load-bearing here — it's what the
// booking link is built from, so a fallback card with the wrong slug sends a
// reader to a 404.
const FALLBACK_SERVICES = [
  {
    slug: "discovery-call",
    emoji: "thinking-seriously",
    title: "Discovery Call",
    duration: "15 mins",
    price: "FREE",
    priceValue: 0,
    description:
      "A quick introduction to understand your goals, challenges, and what kind of mentorship or guidance would benefit you most. Ideal for first-time sessions or exploring collaboration opportunities.",
    isFree: true,
    popular: false,
    requiresConfirmation: true,
  },
  {
    slug: "quick-chat",
    emoji: "happy-looking-to-right",
    title: "Quick Chat",
    duration: "30 mins",
    price: "$35",
    priceValue: 35,
    description:
      "Need advice on a technical challenge, career decision, or team issue? Let's jump on a focused call and tackle it together. Fast, actionable insights, no fluff.",
    isFree: false,
    popular: false,
  },
  {
    slug: "career-guidance",
    emoji: "energy",
    title: "Career Guidance",
    duration: "60 mins",
    price: "$65",
    priceValue: 65,
    description:
      "Get personalized direction on career growth, skill development, or breaking into your next role. Perfect for developers looking to level up or transition into leadership.",
    isFree: false,
    popular: false,
  },
  {
    slug: "resume-review",
    emoji: "smiling-I-am-pro",
    title: "Resume Review",
    duration: "30 mins",
    price: "$50",
    priceValue: 50,
    description:
      "We'll go through your CV together and refine it for maximum impact. You'll get clear, actionable feedback to help you stand out for the roles you want.",
    isFree: false,
    popular: false,
  },
  {
    slug: "interview-prep",
    emoji: "cool",
    title: "Interview Prep & Tips",
    duration: "30 mins",
    price: "$60",
    priceValue: 60,
    description:
      "Get ready to impress. We'll walk through common questions, mindset tips, and strategies to confidently tackle technical and behavioral interviews.",
    isFree: false,
    popular: false,
  },
  {
    slug: "mock-interview",
    emoji: "question",
    title: "Mock Interview",
    duration: "60 mins",
    price: "$85",
    priceValue: 85,
    description:
      "A real interview simulation followed by detailed feedback. We'll identify strengths, improvement areas, and tailor strategies to help you succeed in your next opportunity.",
    isFree: false,
    popular: false,
  },
  {
    slug: "one-on-one-mentorship",
    emoji: "happy-energy",
    title: "1:1 Mentorship",
    duration: "Monthly · 30 min",
    price: "$200/month",
    priceValue: 200,
    description:
      "Ongoing, personalized guidance designed to accelerate your growth. Ideal for developers and engineers seeking long-term improvement in technical, leadership, or communication skills.",
    isFree: false,
    popular: true,
    requiresConfirmation: true,
  },
]

// "5.0 Average Rating" was removed on 2 Sep 2026. It is not a number that had
// drifted out of date; it is unsourceable by construction. `Review` holds zero
// rows, no rating has ever been collected from anyone, and `MentorshipBooking`
// holds exactly one row: a free discovery call that was never delivered. This is
// the same class of claim `d794ca1` stripped off /testimonials on 29 Aug, and it
// was sitting on the page that sells up to £600/month.
//
// The other three stay because they describe a 16-year career rather than this
// platform's history, which is a claim only Gaz can confirm. That question is an
// OWNER/DECIDE in the task file rather than something to guess at here.
const stats = [
  { icon: TbAward, value: "16+", label: "Years Experience" },
  { icon: TbUsers, value: "100+", label: "Mentees Guided" },
  { icon: TbCalendar, value: "500+", label: "Sessions Delivered" },
]

function MentorshipPage({ items: propItems = [], services: servicesProp }: { items?: any[]; services?: any[] }) {
  // Services come from the API (admin-managed); fall back to the built-in list.
  const services = servicesProp && servicesProp.length ? servicesProp : FALLBACK_SERVICES

  // Derived during render, NOT loaded in an effect.
  //
  // This page used to hold `loading` state that started true and only flipped
  // inside a client useEffect — so the server rendered "Loading…" and nothing
  // else. Crawlers fetch HTML and don't run JS, so Google saw a spinner where
  // the copy for a £600/month offer should be, and every article that links
  // here was pointing at an empty page.
  //
  // The effect never needed to be one: `propItems` already arrives from the
  // route loader, server-side. It was copying a prop into state and calling
  // that "loading".
  const testimonials = useMemo(
    () => ({
      data: (propItems ?? []).slice(0, 15).map((item: any) => ({
        ...item,
        createdAt: item.createdAt || item.created_at,
        updatedAt: item.updatedAt || item.updated_at,
        publishedAt: item.publishedAt || item.published_at,
        content_md: item.content_md || "",
      })),
    }),
    [propItems],
  )

  // Genuinely client-only: measuring rendered DOM has no server equivalent.
  useEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector("header") as HTMLElement | null
      const stickyNavEl = document.querySelector(".sticky.top-0") as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty("--header-nav-height", `${offset}px`)
    }
    
    measure()
    requestAnimationFrame(() => {
      measure()
    })
    
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  return (
    <>

      <div className="bg-gray-50 min-h-screen text-[#232323]">
        <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
          <ContentColumn anchor="mentorship-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Even split, matching /learnings: the [1.4fr_1fr] here was sized
                for a single round portrait and squashes a 2x2 grid. */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 w-fit mb-8 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
                <TbAward className="text-base" aria-hidden="true" />
                Principal Engineer · Team Leader
              </span>

              <h1 className="font-heading text-h1 font-bold text-gray-50 mb-6">
                <InlineHighlight color="red">Mentorship</InlineHighlight>
              </h1>

              <p className="text-body-lg font-body text-gray-50/85 mb-5">
                With over a decade of hands-on experience in software engineering, I've grown into a Principal Engineer and Team Leader passionate about architecting scalable systems and empowering people.
              </p>

              <p className="text-body-lg font-body text-gray-50/85 mb-10">
                I help developers and teams thrive through technical excellence, real-world insights, and continuous learning. Whether you're solving engineering challenges or shaping your career path — let's make it happen together.
              </p>

              <p className="text-body-sm font-body text-gray-50/75 mb-10 max-w-xl">
                System design and leadership notes:{" "}
                <a
                  href="/newsletter"
                  className="text-brand-sky underline decoration-brand-sky/50 hover:opacity-90"
                >
                  the newsletter
                </a>
                {" · "}
                <a
                  href="https://www.youtube.com/@gazarbreakpoint"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-sky underline decoration-brand-sky/50 hover:opacity-90"
                >
                  Gazar Breakpoint
                </a>
                {" · "}
                <a
                  href="https://www.linkedin.com/in/gazar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-sky underline decoration-brand-sky/50 hover:opacity-90"
                >
                  LinkedIn
                </a>
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/mentorship/book/discovery-call"
                  {...trackAttrs(CTA.bookCall, 'mentorship_page')}
                  className="group inline-flex items-center justify-center gap-2 bg-gray-50 text-gray-900 font-heading text-cta px-8 py-4 rounded-smooth hover:bg-gray-50/95 transition-colors"
                >
                  Book Discovery Call
                  <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
                <a
                  href="#services"
                  className="inline-flex items-center justify-center gap-2 bg-transparent text-gray-50 border-2 border-gray-50/40 font-heading text-cta px-8 py-4 rounded-smooth hover:border-gray-50/60 hover:bg-gray-50/10 transition-colors"
                >
                  View Programs
                </a>
              </div>
            </div>
            {/* Four scenes of the work rather than one portrait of me: the page
                sells the sessions, and the face is already on /about. */}
            <HeroArtGrid images={HERO_ART.mentorship} />
            </div>
          </ContentColumn>
        </section>

        <section className="border-b border-border-subtle bg-white">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-label font-body text-ink-muted">
                <li>
                  <Link
                    to="/"
                    className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                  >
                    Home
                  </Link>
                </li>
                <li className="font-heading text-gray-900">/</li>
                <li className="font-heading text-gray-900" aria-current="page">
                  Mentorship
                </li>
              </ol>
            </nav>
          </div>
        </section>

        <section id="services" className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 px-5 py-2 mb-6 rounded-smooth border border-border-subtle bg-brand-cream">
              <TbUsers className="text-base text-brand-navy" aria-hidden="true" />
              <span className="font-heading text-overline uppercase text-brand-navy">
                Mentorship Programs
              </span>
            </div>
            <h2 className="font-heading text-h2 font-bold text-brand-navy mb-5">
              Pick what <InlineHighlight color="sky">fits</InlineHighlight>
            </h2>
            <p className="text-base md:text-lg text-ink-soft font-body max-w-2xl mx-auto leading-[1.7]">
              Every session is remote, focused, and built around your specific situation. No generic advice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="relative flex flex-col bg-white border border-border-subtle rounded-smooth-lg p-8 hover:border-border-strong transition-colors duration-150"
              >
                {service.popular && (
                  <span className="absolute -top-4 right-6 inline-flex items-center gap-2 px-4 py-1.5 bg-brand-navy text-white text-xs font-heading uppercase tracking-[0.2em] rounded-smooth">
                    <TbStar className="w-3.5 h-3.5" aria-hidden="true" />
                    Top Pick
                  </span>
                )}

                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 flex items-center justify-center border border-border-subtle rounded-smooth overflow-hidden">
                    <Emoji name={service.emoji} className="w-full h-full" />
                  </div>
                </div>

                <h3 className="font-heading text-h3 font-bold text-brand-navy mb-2">
                  {service.title}
                </h3>
                <p className="text-body-sm font-body text-ink-soft mb-4">
                  {service.description}
                </p>
                <div className="mt-auto pt-4 border-t border-border-subtle">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-overline font-heading uppercase text-ink-muted">
                      {service.duration}
                    </span>
                    <span className="font-heading text-h4 font-bold text-brand-navy shrink-0">
                      {service.price}
                    </span>
                  </div>
                  {/* The card is the whole pitch, so the ask belongs on it. This
                      used to be a display-only tile with the only CTA up in the
                      hero, which meant a reader sold on Mock Interview had to
                      scroll back up and land on a generic discovery call. */}
                  <Link
                    to={`/mentorship/book/${service.slug}`}
                    {...trackAttrs(CTA.bookCall, 'mentorship_card', { target: service.slug })}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-heading text-cta px-5 py-3 rounded-smooth hover:opacity-90 transition-opacity"
                  >
                    {service.isFree ? 'Book this call' : 'See times'}
                    <FaArrowRight className="text-xs" aria-hidden="true" />
                  </Link>
                  <p className="text-caption font-body text-ink-muted mt-2">
                    {service.isFree
                      ? 'Free. Pick a time, I confirm within 24 hours.'
                      : "Pick a time and pay. Refunded in full if I can't make it work."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white py-16">
          <ContentColumn anchor="mentorship-stats" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-6">
              {stats.map((stat) => {
                const IconComponent = stat.icon
                return (
                  <div key={stat.label} className="text-center">
                    <IconComponent className="text-3xl text-brand-navy mx-auto mb-2" />
                    <div className="font-heading text-h2 font-bold text-brand-navy mb-1">
                      {stat.value}
                    </div>
                    <div className="text-overline font-heading uppercase text-ink-muted">
                      {stat.label}
                    </div>
                  </div>
                )
              })}
            </div>
          </ContentColumn>
        </section>

        {testimonials.data.length > 0 && (
          <section className="bg-brand-cream py-16 border-t border-border-subtle">
            <ContentColumn anchor="mentorship-testimonials" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading text-h2 font-bold text-brand-navy mb-12 text-center">
                In their <InlineHighlight color="sky">own words</InlineHighlight>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.data.slice(0, 6).map((testimonial: any) => (
                  <div
                    key={testimonial.id}
                    className="bg-white border border-border-subtle rounded-smooth-lg p-6 flex flex-col"
                  >
                    <span
                      className="font-heading text-5xl leading-none text-brand-red mb-3 select-none"
                      aria-hidden="true"
                    >
                      "
                    </span>
                    <p className="font-body text-body italic text-brand-navy line-clamp-5 flex-1">
                      {testimonial.content_md}
                    </p>
                    {testimonial.name && (
                      <div className="mt-5 pt-4 border-t border-border-subtle">
                        <div className="font-heading text-body-sm font-bold text-brand-navy">
                          {testimonial.name}
                        </div>
                        {testimonial.title && (
                          <div className="font-body text-caption text-ink-muted mt-0.5">
                            {testimonial.title}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ContentColumn>
          </section>
        )}
      </div>
    </>
  )
}

export default MentorshipPage
